/**
 * MilestoneRow — single step in the order investigation timeline.
 *
 * Renders: status icon | title | status badge | timestamp | summary
 * Clickable to expand/collapse the MilestoneDetailsPanel.
 */

import { useState } from 'react';
import { CheckCircle2, Circle, HelpCircle, AlertCircle, XCircle, ChevronDown } from 'lucide-react';
import type { InvestigationMilestone, MilestoneStatus } from '../../types/orderInvestigation';
import MilestoneDetailsPanel from './MilestoneDetailsPanel';
import { PROVENANCE_SOFT_BADGE_CLASSES, MILESTONE_STATUS_SOFT_BADGE_CLASSES } from '../../config/colors';
import { formatUkFullDateTime } from '../../lib/calc';

interface MilestoneRowProps {
  milestone: InvestigationMilestone;
  isLast?: boolean;
}

// Tier colours come from the shared colors module (ticket 15): an
// inference is not a confirmation — inferred is violet, never the amber it
// drifted to here. `missing`/`contradictory` are not provenance tiers;
// their badge expressions come from the shared non-tier map in the same
// module.
const STATUS_CONFIG: Record<MilestoneStatus, {
  icon: typeof CheckCircle2;
  iconClass: string;
  badgeClass: string;
  label: string;
  lineClass: string;
}> = {
  observed: {
    icon: CheckCircle2,
    iconClass: 'text-success',
    badgeClass: PROVENANCE_SOFT_BADGE_CLASSES.observed,
    label: 'Observed',
    lineClass: 'border-success/30',
  },
  declared: {
    icon: Circle,
    iconClass: 'text-ring',
    badgeClass: PROVENANCE_SOFT_BADGE_CLASSES.declared,
    label: 'Declared',
    lineClass: 'border-ring/30',
  },
  inferred: {
    icon: HelpCircle,
    iconClass: 'text-provenance-inferred',
    badgeClass: PROVENANCE_SOFT_BADGE_CLASSES.inferred,
    label: 'Inferred',
    lineClass: 'border-provenance-inferred/30',
  },
  missing: {
    icon: AlertCircle,
    iconClass: 'text-muted-foreground',
    badgeClass: MILESTONE_STATUS_SOFT_BADGE_CLASSES.missing,
    label: 'Missing',
    lineClass: 'border-accent/30',
  },
  contradictory: {
    icon: XCircle,
    iconClass: 'text-destructive',
    badgeClass: MILESTONE_STATUS_SOFT_BADGE_CLASSES.contradictory,
    label: 'Contradictory',
    lineClass: 'border-destructive/30',
  },
};

function formatTimestamp(iso?: string): string | null {
  if (!iso) return null;
  return formatUkFullDateTime(iso, iso);
}

export default function MilestoneRow({ milestone, isLast }: MilestoneRowProps) {
  const [expanded, setExpanded] = useState(false);
  const config = STATUS_CONFIG[milestone.status];
  const Icon = config.icon;
  const formattedTime = formatTimestamp(milestone.bestTimestamp);

  return (
    <div className="relative">
      {/* Vertical connector line */}
      {!isLast && (
        <div
          className={`absolute left-4 top-8 bottom-0 w-0 border-l-2 border-dashed ${config.lineClass}`}
        />
      )}

      {/* Clickable row */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left flex items-start gap-3 p-2 -ml-1 rounded-lg hover:bg-secondary/30 transition-colors group"
      >
        {/* Icon */}
        <div className="shrink-0 mt-0.5">
          <Icon className={`w-6 h-6 ${config.iconClass}`} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white text-sm">{milestone.title}</span>
            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${config.badgeClass}`}>
              {config.label}
            </span>
            {formattedTime && (
              <span className="text-xs text-muted-foreground font-mono">{formattedTime}</span>
            )}
            {/* Expand indicator */}
            <ChevronDown
              className={`w-3.5 h-3.5 text-accent group-hover:text-muted-foreground transition-transform duration-200 ml-auto shrink-0 ${
                expanded ? 'rotate-180' : ''
              }`}
            />
          </div>

          {/* Summary */}
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{milestone.summary}</p>

          {/* Expandable details */}
          <MilestoneDetailsPanel evidence={milestone.evidence} expanded={expanded} />
        </div>
      </button>
    </div>
  );
}
